import { Select, Spin, Button, Input, message, Tooltip, Typography } from 'antd'
import { useState, useEffect } from 'react'
import { getReplies } from 'src/api/social'
import { ADMINCHECKSTATE } from 'src/constants'
import {
  EditOutlined,
  DeleteOutlined,
  LikeOutlined,
  CommentOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons'
import { extractTextContent } from 'src/utils/stringUtil'
import { longToTimestamp } from 'src/utils/timestamp'
const { Text } = Typography

const Comment = ({
  comment,
  userId,
  userData,
  onReply,
  onEdit,
  onDelete,
  onLike,
  onAdminStateChange,
  level = 0,
  refreshReplies,
}) => {
  const [showReplies, setShowReplies] = useState(false)
  const [replies, setReplies] = useState([])
  const [repliesLoading, setRepliesLoading] = useState(false)
  const [repliesPage, setRepliesPage] = useState(1)
  const [repliesPageSize] = useState(5)
  const [repliesTotal, setRepliesTotal] = useState(0)

  const handleAdminStateChange = async (value, comment) => {
    await onAdminStateChange(value, comment)
    if (showReplies) {
      fetchReplies(1)
    }
  }
  const handleLike = async (comment) => {
    await onLike(comment)
    if (showReplies) {
      fetchReplies(1)
    }
  }
  useEffect(() => {
    if (showReplies && refreshReplies) {
      fetchReplies(1)
    }
  }, [refreshReplies])

  const fetchReplies = async (page = repliesPage) => {
    if (comment.comments === 0) return
    setRepliesLoading(true)
    try {
      const response = await getReplies(
        comment.id,
        userId,
        page - 1,
        repliesPageSize
      )
      const transformedReplies = await Promise.all(
        response.data.map((item) => ({
          ...item,
          key: item.id,
          user_id: item.user?.user_id || 'unknown',
          username: item.user?.username || 'Unknown User',
          date: longToTimestamp(item.created_on),
          formattedText: item.deleted
            ? ''
            : extractTextContent(item.text || ''),
          comments: item.comments || 0,
          replies: [],
          deleted: item.deleted || false,
        }))
      )
      setReplies((prev) =>
        page === 1 ? transformedReplies : [...prev, ...transformedReplies]
      )
      setRepliesTotal(response.total_items || 0)
      setShowReplies(true)
    } catch (error) {
      message.error('Failed to fetch replies.')
    } finally {
      setRepliesLoading(false)
    }
  }

  const handleLoadMore = () => {
    const nextPage = repliesPage + 1
    setRepliesPage(nextPage)
    fetchReplies(nextPage)
  }

  if (comment.deleted) {
    return (
      <div
        style={{
          marginLeft: level * 20,
          padding: '10px',
          borderLeft: level > 0 ? '2px solid #e8e8e8' : 'none',
        }}
      >
        <div style={{ color: '#888', fontStyle: 'italic' }}>
          This comment has been deleted
        </div>
        {comment.comments > 0 && (
          <Button
            type="link"
            onClick={() =>
              showReplies ? setShowReplies(false) : fetchReplies()
            }
            style={{ marginTop: '5px' }}
          >
            {showReplies ? <UpOutlined /> : <DownOutlined />}{' '}
            {showReplies ? 'Hide' : 'Show'} {comment.comments} repl
            {comment.comments === 1 ? 'y' : 'ies'}
          </Button>
        )}
        {showReplies && (
          <Spin spinning={repliesLoading}>
            {replies.map((reply) => (
              <Comment
                key={reply.id}
                comment={reply}
                userId={userId}
                userData={userData}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                onLike={onLike}
                onAdminStateChange={onAdminStateChange}
                level={level + 1}
                refreshReplies={refreshReplies}
              />
            ))}
            {replies.length < repliesTotal && level <= 5 && (
              <Button
                type="link"
                onClick={handleLoadMore}
                style={{ marginTop: '10px' }}
                aria-label={`Load ${
                  repliesTotal - replies.length
                } more comments`}
              >
                Load more comments ({repliesTotal - replies.length} more)
              </Button>
            )}
            {replies.length < repliesTotal && level > 5 && (
              <a
                href={`/comments/${comment.id}`}
                style={{ marginTop: '10px', display: 'block' }}
                aria-label="Continue this comment thread"
              >
                Continue this thread
              </a>
            )}
          </Spin>
        )}
      </div>
    )
  }

  return (
    <div
      style={{
        marginLeft: level * 20,
        padding: '10px',
        borderLeft: level > 0 ? '2px solid #e8e8e8' : 'none',
        backgroundColor: comment.text_bad_content ? '#781515' : 'transparent',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '5px',
        }}
      >
        <Text style={{ color: '#f3109e' }}>{comment.username}</Text>
        <span
          style={{
            marginLeft: '10px',
            color: '#888',
            fontStyle: 'italic',
            fontSize: '12px',
          }}
        >
          {comment.date}
        </span>
      </div>
      <div>
        {comment.formattedText.length > 100 ? (
          <span>
            {comment.formattedText.substring(0, 100)}
            <Tooltip
              title={comment.formattedText}
              placement="right"
              overlayStyle={{ maxWidth: '300px' }}
            >
              <span
                style={{
                  cursor: 'pointer',
                  color: '#888',
                }}
                aria-label="Show full comment text"
              >
                ...
              </span>
            </Tooltip>
          </span>
        ) : (
          comment.formattedText
        )}
      </div>
      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            type="text"
            icon={<LikeOutlined style={{ color: '#1890ff' }} />}
            onClick={() => handleLike(comment)}
            style={{ marginRight: '10px' }}
          >
            {comment.likes} Like
          </Button>
          <Button
            type="text"
            icon={<CommentOutlined style={{ color: '#1890ff' }} />}
            onClick={() => onReply(comment)}
            style={{ marginRight: '10px' }}
          >
            Reply
          </Button>
        </div>
        <div
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
        >
          <>
            {comment.user?.type !== 'USER' && (
              <Button
                type="text"
                icon={<EditOutlined style={{ color: '#1890ff' }} />}
                onClick={() => onEdit(comment)}
                style={{ marginRight: '10px' }}
              />
            )}
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onDelete(comment)}
              style={{ marginRight: '10px' }}
            />
          </>

          <Select
            value={comment.admin_check_state}
            onChange={(value) => handleAdminStateChange(value, comment)}
            options={ADMINCHECKSTATE}
            style={{ width: '120px' }}
          />
        </div>
      </div>
      {comment.comments > 0 && (
        <Button
          type="link"
          onClick={() => (showReplies ? setShowReplies(false) : fetchReplies())}
          style={{ marginTop: '5px' }}
        >
          {showReplies ? <UpOutlined /> : <DownOutlined />}{' '}
          {showReplies ? 'Hide' : 'Show'} {comment.comments} repl
          {comment.comments === 1 ? 'y' : 'ies'}
        </Button>
      )}
      {showReplies && (
        <Spin spinning={repliesLoading}>
          {replies.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              userId={userId}
              userData={userData}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onLike={onLike}
              onAdminStateChange={onAdminStateChange}
              level={level + 1}
              refreshReplies={refreshReplies}
            />
          ))}
          {replies.length < repliesTotal && level <= 5 && (
            <Button
              type="link"
              onClick={handleLoadMore}
              style={{ marginTop: '10px' }}
              aria-label={`Load ${repliesTotal - replies.length} more comments`}
            >
              Load more comments ({repliesTotal - replies.length} more)
            </Button>
          )}
          {replies.length < repliesTotal && level > 5 && (
            <a
              href={`/comments/${comment.id}`}
              style={{ marginTop: '10px', display: 'block' }}
              aria-label="Continue this comment thread"
            >
              Continue this thread
            </a>
          )}
        </Spin>
      )}
    </div>
  )
}

export default Comment
